import { useQuery } from "@apollo/client/react";
import { Stack, styled, Typography } from "@mui/material";
import { DataGridProProps, GridEventListener, GridRowParams, GridSortModel } from "@mui/x-data-grid-pro";
import { CloudArrowUpIcon } from "@phosphor-icons/react/dist/csr/CloudArrowUp";
import { useEffect, useMemo, useState } from "react";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import { GqlError } from "../../components/common/GqlError";
import { getSortBy, getSortDirection } from "../../components/DataGridUtils";
import useAuth, { DataGridColumns } from "../../context/UserContext";
import {
  Upload,
  ExportDeclarationsDocument,
  ExportDeclarationsQuery,
  ExportDeclarationsQueryVariables,
  UploadsDocument,
  UploadsQuery,
  UploadsQueryVariables,
} from "../../graphql/types";
import { setOrRemoveRangeParam, setOrRemoveSearchParam } from "../../lib/Util";
import { DownloadsForUploadDrawer } from "../../pages/uploads/DownloadsForUploadDrawer";
import { UploadFilters } from "./UploadFilters";
import { UploadsDataGrid } from "./UploadsDataGrid";
import {
  getDefaultUploadFilters,
  getUploadsDatagridQueryVariables,
  getUploadsNodes,
  IUploadsFilter,
} from "./uploadsDatagridUtils";

const Container = styled(Stack)({
  width: "100%",
  height: "80vh",
  minHeight: "400px",
  minWidth: "200px",
  borderRadius: "8px",
});

const initialVisibilityModelUploadsDatagrid: Partial<Record<DataGridColumns, boolean>> = {
  [DataGridColumns.ReportDateTime]: true,
  [DataGridColumns.Created]: false,
  [DataGridColumns.Project]: true,
  [DataGridColumns.Name]: true,
  [DataGridColumns.Purpose]: true,
  [DataGridColumns.Description]: false,
  [DataGridColumns.Status]: true,
  [DataGridColumns.Actions]: true,
  [DataGridColumns.Started]: true,
  [DataGridColumns.Finished]: false,
  [DataGridColumns.UploadDuration]: false,
  [DataGridColumns.QueuedDuration]: false,
  [DataGridColumns.Destination]: true,
};

const updateSearchParams = (params: URLSearchParams, filter: IUploadsFilter, setSearchParams: SetURLSearchParams) => {
  const {
    projects,
    exportNames,
    search,
    status,
    reportRange,
    purpose,
    uploadedRange,
    destination,
    sortBy,
    sortDirection,
  } = filter;
  const updatedParams = new URLSearchParams(params.toString());
  setOrRemoveSearchParam("project", projects.join(","), updatedParams);
  setOrRemoveSearchParam("uploadName", exportNames.join(","), updatedParams);
  setOrRemoveSearchParam("search", search, updatedParams);
  setOrRemoveSearchParam("status", status, updatedParams);
  setOrRemoveSearchParam("purpose", purpose, updatedParams);
  setOrRemoveSearchParam("destination", destination, updatedParams);
  setOrRemoveSearchParam("sortBy", sortBy, updatedParams);
  setOrRemoveSearchParam("sortDirection", sortDirection, updatedParams);
  setOrRemoveRangeParam("start", "end", updatedParams, reportRange);
  setOrRemoveRangeParam("uploaded_start", "uploaded_end", updatedParams, uploadedRange);
  if (updatedParams.toString() !== params.toString()) setSearchParams(updatedParams);
};

const UploadsDataGridWithQuery = ({
  excludeFields,
  variables,
  useDefaultDateRange = true,
  disableDataGridFilter,
}: {
  excludeFields?: string[];
  variables?: Partial<IUploadsFilter>;
  useDefaultDateRange?: boolean;
  disableDataGridFilter?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<IUploadsFilter>(
    getDefaultUploadFilters(searchParams, useDefaultDateRange, variables)
  );
  const [prevInputsKey, setPrevInputsKey] = useState(
    () => `${searchParams.toString()}|${useDefaultDateRange}|${JSON.stringify(variables)}`
  );

  const { user } = useAuth();

  useEffect(() => {
    updateSearchParams(searchParams, filter, setSearchParams);
  }, [filter, searchParams, setSearchParams]);

  if (variables) {
    const currentInputsKey = `${searchParams.toString()}|${useDefaultDateRange}|${JSON.stringify(variables)}`;
    if (currentInputsKey !== prevInputsKey) {
      setPrevInputsKey(currentInputsKey);
      setFilter(getDefaultUploadFilters(searchParams, useDefaultDateRange, variables));
    }
  }

  const queryVariables = getUploadsDatagridQueryVariables(filter);

  const { data, loading, fetchMore, error, refetch } = useQuery<UploadsQuery, UploadsQueryVariables>(UploadsDocument, {
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const treeDataQuery = useQuery<ExportDeclarationsQuery, ExportDeclarationsQueryVariables>(ExportDeclarationsDocument);

  const onRowsScrollEnd: DataGridProProps["onRowsScrollEnd"] = () => {
    if (!data?.uploads?.pageInfo.hasNextPage) return;
    fetchMore({
      variables: {
        after: data?.uploads?.pageInfo?.endCursor || undefined,
      },
    });
  };

  const onSortModelChange = (model: GridSortModel) => {
    const field = model?.[0]?.field;
    const direction = model?.[0]?.sort;
    const sortBy = getSortBy(field);
    const sortDirection = getSortDirection(direction);
    refetch({
      sortBy,
      sortDirection,
    });
    setFilter({
      ...filter,
      sortBy,
      sortDirection,
    });
  };

  const uploads = data?.uploads?.edges.map((edge) => edge?.node as Upload);

  const handleRowClick: GridEventListener<"rowClick"> | undefined = ({ row }: GridRowParams<Upload>) => {
    setSearchParams((urlParams) => {
      if (row.id) {
        urlParams.set("selectedUploadId", row.id);
        urlParams.set("selectedUploadName", row.name);
      }
      return urlParams;
    });
  };

  const handleCloseDrawer = () => {
    setSearchParams((urlParams) => {
      urlParams.delete("selectedUploadId");
      urlParams.delete("selectedUploadName");
      return urlParams;
    });
  };

  const { selectedNodes, nonSelectedNodes } = useMemo(
    () => getUploadsNodes(treeDataQuery.data, filter),
    [treeDataQuery.data, filter]
  );
  const uploadId = searchParams.get("selectedUploadId");

  return (
    <Container>
      {error ? (
        <GqlError error={error} />
      ) : (
        <>
          {disableDataGridFilter ? null : (
            <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: "28px" }}>
              <Stack direction="row" sx={{ gap: "16px", alignItems: "center" }}>
                <CloudArrowUpIcon size={32} />
                <Typography variant="heading.three">Uploads</Typography>
              </Stack>
              <UploadFilters
                loading={treeDataQuery.loading}
                filter={filter}
                setFilter={setFilter}
                queryVariables={queryVariables}
                selectedNodesInFilter={selectedNodes}
                nonSelectedNodesInFilter={nonSelectedNodes}
                datagridPreferencesElement={user.datagridPreferences.uploads}
                preferenceElementToUpdate="uploads"
                initialVisibilityModel={initialVisibilityModelUploadsDatagrid}
              />
            </Stack>
          )}
          <UploadsDataGrid
            datagridPreferencesElement={user.datagridPreferences.uploads}
            rows={uploads}
            onRowsScrollEnd={onRowsScrollEnd}
            filter={filter}
            loading={loading}
            onSortModelChange={onSortModelChange}
            excludeFields={excludeFields}
            handleRowClick={handleRowClick}
            preferenceElementToUpdate="uploads"
            initialVisibilityModel={initialVisibilityModelUploadsDatagrid}
          />
          {uploadId ? <DownloadsForUploadDrawer handleCloseDrawer={handleCloseDrawer} /> : null}
        </>
      )}
    </Container>
  );
};

export { initialVisibilityModelUploadsDatagrid, UploadsDataGridWithQuery };
